import { Button, HoverCard, MultiSelect } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconCopy,
  IconDeviceFloppy,
  IconExclamationCircle,
  IconInfoCircle,
  IconPlus,
  IconTags,
  IconTrash,
} from '@tabler/icons-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useManageTagsDialog } from '~/hooks/tag/useManageTagsDialog';
import { useDuplicateTest } from '~/hooks/test/useDuplicateTest';
import { useTestSuiteDialog } from '~/hooks/useTestSuiteDialog';
import { queryParamToString } from '~/logic/query-param';
import { api } from '~/trpc-extra/api';

export default function TestSettings({ testId }: { testId: string }) {
  const router = useRouter();
  const projectId = queryParamToString(router.query.projectId);
  const trpcUtils = api.useUtils();
  const { isAdmin } = useAuthData();

  const { data: testSuites } = api.test.getTestSuites.useQuery({ testId });
  const { data: tags } = api.test.getTags.useQuery({ testId });
  const { data: testMeta } = api.test.getMeta.useQuery({ id: testId });

  const { data: availableTags = [] } = api.tag.list.useQuery({ projectId });
  const { mutate: updateTest } = api.test.updateTest.useMutation({
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        message: `Tags updated`,
        icon: <IconDeviceFloppy />,
      });
      trpcUtils.test.getTags.invalidate().catch(() => undefined);
      await trpcUtils.test.getTest.invalidate();
      await trpcUtils.test.listTests.invalidate();
    },
    onError: () => {
      notifications.show({
        color: 'red',
        message: `Error updating tags`,
        icon: <IconExclamationCircle />,
      });
    },
  });

  const { mutate: deleteTest, isPending: isDeleting } = api.test.delete.useMutation({
    onMutate: () => {
      notifications.show({
        color: 'teal',
        message: 'Deleting test...',
        icon: <IconTrash />,
      });
    },
    onSuccess: () => {
      notifications.show({
        color: 'teal',
        message: 'Test deleted',
        icon: <IconTrash />,
      });
      // Redirect to test list
      window.location.href = `/project/${projectId}/test`;
    },
    onError: (e) => {
      console.error(e);
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `${e}`,
        icon: <IconExclamationCircle />,
      });
    },
  });

  const { isDuplicating, handleDuplicateClick } = useDuplicateTest({
    testId,
    projectId,
    testMeta,
  });

  const { mutate: updateTestMembership } = api.testSuite.updateTests.useMutation({
    onMutate: () => {
      notifications.show({
        color: 'gray',
        message: `Adding test to suite`,
        icon: <IconDeviceFloppy />,
      });
    },
    onSuccess: () => {
      notifications.show({
        color: 'teal',
        message: `Added test to suite`,
        icon: <IconDeviceFloppy />,
      });
      trpcUtils.testSuite.list.invalidate().catch(() => undefined);
      trpcUtils.testSuite.get.invalidate().catch(() => undefined);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `${e}`,
        icon: <IconExclamationCircle />,
      });
    },
  });

  const addToTestGroup = useTestSuiteDialog({
    onSuiteClick: (testSuiteId) => {
      updateTestMembership({
        operation: 'add',
        projectId: projectId!,
        testSuiteId,
        testIds: [testId],
      });
    },
  });

  const manageTagsDialog = useManageTagsDialog();

  const clipboard = useClipboard();

  const { mutateAsync: getTestDefinition } = api.test.getTestDefinition.useMutation();

  const onCopyTestDefinitionClick = async () => {
    const testDefinition = await getTestDefinition({ id: testId, projectId: projectId! });
    const testDefinitionJSON = JSON.stringify(testDefinition);
    clipboard.copy(testDefinitionJSON);
    notifications.show({
      title: 'Test definition copied successfully',
      message: 'The test definition was copied to your clipboard',
    });
  };

  return (
    <div className="max-w-3xl px-8 py-8">
      {/* Tags Section */}
      <section className="mb-12">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-stone-800 dark:text-stone-200">Tags</h2>
          <div className="mt-2 flex items-center gap-2">
            <p className="text-sm text-stone-600 dark:text-stone-400">
              Tags are a way to categorize tests. They can be used to filter tests in the test list.
            </p>
          </div>
          <div className="w-[300px]">
            {(availableTags?.length ?? 0) > 0 && (
              <MultiSelect
                searchable
                className="mt-2"
                placeholder="Select tags"
                data={
                  availableTags?.map((tag) => ({
                    value: tag.id,
                    label: tag.name,
                  })) || []
                }
                value={tags?.map((tag) => tag.id) || []}
                onChange={(value) => {
                  updateTest({ testId, testData: { tags: value } });
                }}
              />
            )}
            <Button
              className="mt-2 flex-shrink-0"
              variant="default"
              leftSection={<IconTags size={14} />}
              onClick={() => manageTagsDialog()}
            >
              Manage tags
            </Button>
          </div>
        </div>
      </section>

      {/* Test Suites Section */}
      <section className="mb-12">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-stone-800 dark:text-stone-200">Test Suites</h2>
          <div className="mt-2 flex items-center gap-2">
            <p className="text-sm text-stone-600 dark:text-stone-400">
              Group tests together to run them as a batch and get consolidated reports
            </p>
            <HoverCard
              width={280}
              shadow="md"
            >
              <HoverCard.Target>
                <IconInfoCircle
                  size={18}
                  className="text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"
                />
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <div className="text-sm">
                  A test suite lets you run multiple tests together. It&apos;s a great way to trigger multiple tests at
                  once and get a consolidated report.
                </div>
              </HoverCard.Dropdown>
            </HoverCard>
          </div>
        </div>

        {/* Test Suite List */}
        {!!testSuites && testSuites.length > 0 && (
          <div className="mb-4">
            <h3 className="mb-2 text-sm font-medium text-stone-600 dark:text-stone-400">Current test suites:</h3>
            <div className="space-y-2">
              {testSuites.map((group) => (
                <Link
                  key={group.id}
                  href={`/project/${projectId}/testSuite/${group.id}`}
                  className="block text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {group.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        <Button
          variant="default"
          onClick={addToTestGroup}
          leftSection={<IconPlus size={14} />}
          className="mt-4"
        >
          Add To Test Suite
        </Button>
      </section>

      {/* Test Management Section */}
      <section>
        <h2 className="mb-6 text-xl font-bold text-stone-800 dark:text-stone-200">Test Management</h2>
        <div className="flex items-center gap-3">
          <Button
            variant="default"
            leftSection={<IconCopy size={14} />}
            loading={isDuplicating}
            onClick={handleDuplicateClick}
          >
            Duplicate Test
          </Button>
          <Button
            variant="outline"
            color="red"
            leftSection={<IconTrash size={14} />}
            loading={isDeleting}
            onClick={() =>
              modals.openConfirmModal({
                title: 'Please confirm',
                children: <span>This will permanently delete the the test.</span>,
                labels: { confirm: 'Delete Test', cancel: 'Cancel' },
                zIndex: 310,
                confirmProps: {
                  color: 'red',
                  loading: isDeleting,
                },
                onConfirm: () => deleteTest({ ids: [testId] }),
              })
            }
          >
            Delete Test
          </Button>
          {isAdmin ?
            <Button
              color="orange"
              variant="outline"
              onClick={onCopyTestDefinitionClick}
            >
              <IconCopy
                size={16}
                className="mr-1"
              />
              Copy Definition
            </Button>
          : null}
        </div>
      </section>
    </div>
  );
}
